import React from "react";
import Dragger from "antd/es/upload/Dragger";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {UploadChangeParam, UploadFile} from "antd/es/upload/interface";
import {Button, message, Upload} from "antd";
import {FileOutlined} from "@ant-design/icons";
import classNames from "classnames";
import {
    EditorType,
    ElementsValues,
    ElementsValuesType,
    getElementsValues,
    Nullable,
    UploadsConfig,
} from "@data-force/app";
import {FileTypeResult} from "file-type/browser";
import {RefElementsMap} from "@data-force/app/interfaces/store/actions/payload/schema/schemaElements";
import {ArgsProps} from "antd/es/message";
import {
    batchUploadsAreValid,
    calculateNumberOfTasks,
    determineBucketKey,
    formatString,
    getHistoryParams,
    getTaskPhaseStatus,
    getUploadFileList,
    initUploadsData,
    isEditableTask,
    isPhaseExpired,
    mergeUploadBuckets,
} from "../../utils/common";
import StoreState from "../../interfaces/store/storeState";
import {RemoveSelectedFileAction, SetSelectedFilesAction} from "../../interfaces/store/actions/uploadFiles";
import {removeSelectedFile, setSelectedFiles} from "../../store/actions/uploadFiles";
import "./FilePicker.scss";
import HistoryParams from "../../interfaces/historyParams";
import {Result} from "../../types/types";
import {setJobMaskData} from "../../store/actions/job";
import {JobMaskDataPayload} from "../../interfaces/store/actions/payload/job";
import {SetJobMaskDataAction} from "../../interfaces/store/actions/job";
import {FileTypeAndExtension, UploadsData} from "../../interfaces/store/actions/payload/fileValidation";
import {TaskFile} from "../../interfaces/store/actions/payload/uploadFiles";
import {
    ActiveTaskPrivates, Status, Task, TaskEntity,
} from "../../interfaces/store/actions/payload/task";
import {BucketCount, SelectedFiles, UploadsBucket} from "../../interfaces/store/reducers/uploadFilesState";
import "./SingleFilePicker.scss";
import {updateActiveTaskPrivates} from "../../store/actions/task";
import {
    beforeUploadHandler,
    checkIfImageUrl,
    createBucketLimitAlert,
    createDeleteIcon,
    createPreviewIcon,
    createReopenedFileAlert,
    createUploadFilesLimitAlert,
    fileIsValid,
    generatePreviewFile,
    getAvailableHolderIndex,
    getNameFromMediaUrl,
    handleTypeAndMediaInfo,
    mapFileToTask,
    renderFallbackIcon,
    renderThumbnail,
} from "./filePickerUtils";
import {FileValidity, HandleMediaInfoResult, Thumbnails} from "../../interfaces/components/FilePickerTypes";
import {HTML_SUPPORTED_AUDIO, HTML_SUPPORTED_FILE_EXTENSIONS, HTML_SUPPORTED_IMAGE} from "../../utils/constants";
import {UpdateActiveTaskPrivatesAction} from "../../interfaces/store/actions/task";
import {AddAlertsAction, ClearAlertsAction} from "../../interfaces/store/actions/layout";
import {addAlerts, clearAlerts} from "../../store/actions/layout";
import {FileSelectionType} from "../../interfaces/store/actions/payload/layout";

interface StateProps {
    translate: TranslateFunction
    fileSizeLimit: number
    batchUploads: UploadsConfig[]
    collectionRefElements: RefElementsMap
    elementsValues: ElementsValues
    activeTaskFile: Nullable<UploadFile>
    activeTask: Nullable<Task>
    tasks: TaskEntity[]
    mappedTaskIds: string[]
    numberOfRequiredFiles: number
    selectedFiles: SelectedFiles
    fileSelectionType: Nullable<FileSelectionType>
    showOverlay: boolean
    uploadsData: UploadsData
    signedMediaUrl: Nullable<string>
    fileName: Nullable<string>
    upload: Nullable<string>
    isTaskEditable: boolean
    activeTaskFileSelected: boolean
    validBatchUploadsConfig: boolean
    editor: Nullable<EditorType>
    finishedTaskIds: string[]
    uploadedFileIsSelected: boolean
    bucketCount: BucketCount
    isReadOnly: boolean
}

interface DispatchProps {
    setSelectedFiles(payload: SelectedFiles): SetSelectedFilesAction

    removeSelectedFile(payload: UploadFile): RemoveSelectedFileAction

    setJobMaskData(payload: JobMaskDataPayload): SetJobMaskDataAction

    updateActiveTaskPrivates(payload: ActiveTaskPrivates): UpdateActiveTaskPrivatesAction

    clearAlerts(): ClearAlertsAction

    addAlerts(payload: ArgsProps[]): AddAlertsAction
}

type Props = DispatchProps & StateProps;

class SingleFilePicker extends React.Component<Props> {
    private static readonly translatePath: string = "components.SingleFilePicker.SingleFilePicker";

    private static readonly filePickerTranslatePath: string = "components.FilePicker.FilePicker";

    constructor(props: Props) {
        super(props);

        this.onChangeHandler = this.onChangeHandler.bind(this);
        this.updateFileList = this.updateFileList.bind(this);
        this.removeSignedMediaUrl = this.removeSignedMediaUrl.bind(this);
        this.isDisabledFileDisplayPreview = this.isDisabledFileDisplayPreview.bind(this);
        this.renderFileDisplay = this.renderFileDisplay.bind(this);
        this.previewUrl = this.previewUrl.bind(this);
        this.renderDeleteIcon = this.renderDeleteIcon.bind(this);
        this.renderPreviewIcon = this.renderPreviewIcon.bind(this);
        this.clearAlertMessages = this.clearAlertMessages.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        const {
            activeTaskFile,
            activeTask,
            tasks,
            numberOfRequiredFiles,
            selectedFiles,
            fileSelectionType,
            showOverlay,
            signedMediaUrl,
            fileName,
            upload,
            isTaskEditable,
            activeTaskFileSelected,
            validBatchUploadsConfig,
            finishedTaskIds,
            uploadedFileIsSelected,
            mappedTaskIds,
        }: Props = this.props;
        return (
            activeTaskFile !== nextProps.activeTaskFile
            || activeTask !== nextProps.activeTask
            || tasks !== nextProps.tasks
            || numberOfRequiredFiles !== nextProps.numberOfRequiredFiles
            || selectedFiles !== nextProps.selectedFiles
            || fileSelectionType !== nextProps.fileSelectionType
            || showOverlay !== nextProps.showOverlay
            || signedMediaUrl !== nextProps.signedMediaUrl
            || fileName !== nextProps.fileName
            || upload !== nextProps.upload
            || isTaskEditable !== nextProps.isTaskEditable
            || activeTaskFileSelected !== nextProps.activeTaskFileSelected
            || validBatchUploadsConfig !== nextProps.validBatchUploadsConfig
            || finishedTaskIds !== nextProps.finishedTaskIds
            || uploadedFileIsSelected !== nextProps.uploadedFileIsSelected
            || mappedTaskIds.length !== nextProps.mappedTaskIds.length
        );
    }

    onChangeHandler({fileList, file}: UploadChangeParam): void {
        this.clearAlertMessages();
        this.props.setJobMaskData({visible: true, messageId: "validatingFiles", displaySpinner: true});
        if (file.status === "removed") {
            this.removeFile(file);
        } else {
            const currentFile: UploadFile = fileList.find(
                (current: UploadFile): boolean => current.uid === file.uid,
            ) as UploadFile;
            handleTypeAndMediaInfo(currentFile).then(
                (values: HandleMediaInfoResult): void => this.updateFileList(...values),
            );
        }
    }

    /**
     * Actual removal of current file from list of valid files, hiding of mask
     * @param {UploadFile} file
     */
    removeFile(file: UploadFile): void {
        this.clearAlertMessages();
        this.props.removeSelectedFile(file);
        this.props.setJobMaskData({visible: false, messageId: "", displaySpinner: true});
    }

    /**
     * Clear alerts from both, screen and redux store.
     *
     * @return {void}
     */
    clearAlertMessages(): void {
        message.destroy();
        this.props.clearAlerts();
    }

    removeSignedMediaUrl(): void {
        this.props.updateActiveTaskPrivates({signedMediaUrl: null});
        this.props.setJobMaskData({visible: false, messageId: "", displaySpinner: true});
    }

    /**
     *  Updates store on files upload.
     *
     *  @param {UploadFile} file
     *  @param sniffedType from {Nullable<FileTypeResult>}
     *  @param fileMediaInfo from {Nullable<Result>}
     *  @param {FileTypeAndExtension} fileTypeAndExtension
     */
    updateFileList(
        file: UploadFile,
        sniffedType: Nullable<FileTypeResult>,
        fileMediaInfo: Nullable<Result>,
        fileTypeAndExtension: FileTypeAndExtension,
    ): void {
        const {filePickerTranslatePath}: typeof SingleFilePicker = SingleFilePicker;
        const {
            fileSizeLimit,
            selectedFiles,
            elementsValues,
            translate,
            collectionRefElements,
            tasks,
            activeTask,
            mappedTaskIds,
            fileSelectionType,
            numberOfRequiredFiles,
            uploadsData,
            upload,
            validBatchUploadsConfig,
            finishedTaskIds,
            fileName: activeTaskUploadedFileName,
            bucketCount,
        }: Props = this.props;
        const {fileType, fileExtension}: FileTypeAndExtension = fileTypeAndExtension;
        const maxFileSize: number = fileSizeLimit * 1024 * 1024;
        const {size, name, uid}: UploadFile = file;
        const {uploadsHolders}: UploadsData = uploadsData;

        const {isFileValid, validUploadsHolders, validationAlerts}: FileValidity = fileIsValid({
            fileMediaInfo,
            fileMimeType: fileType,
            fileName: name,
            fileExtension,
            uid,
            elementsValues,
            translate,
            collectionRefElements,
            uploadsData,
            translatePath: filePickerTranslatePath,
            upload,
        });
        if (validationAlerts.length) {
            this.props.addAlerts(validationAlerts);
        }

        const availableHolderIndex: number = getAvailableHolderIndex({
            uploadsHolders: validUploadsHolders,
            bucketFilesMap: selectedFiles,
            validBatchUploadsConfig,
            bucketCount,
        });
        const validFiles: UploadFile[] = getUploadFileList(selectedFiles);
        const fileSelectable: boolean = size !== undefined && size < maxFileSize
            && isFileValid
            && validFiles.length < numberOfRequiredFiles;

        if (fileSelectable && (availableHolderIndex > -1 || !uploadsHolders.length)) {
            const isDuplicate: boolean = new Set(
                [
                    ...validFiles.map((validFile: UploadFile): string => validFile.name),
                    ...uploadsData.fileNamesList.filter(
                        (uploadName: string): boolean => uploadName !== activeTaskUploadedFileName,
                    ),
                ],
            ).has(name);
            if (!isDuplicate) {
                const taskFile: Nullable<TaskFile> = mapFileToTask({
                    uploadFile: file,
                    activeTask,
                    fileSelectionType,
                    mappedTaskIds,
                    tasks,
                    finishedTaskIds,
                });
                if (taskFile) {
                    const bucketKey: string = determineBucketKey({
                        useBucketConfig: !!uploadsHolders.length,
                        validBatchUploadsConfig,
                        uploadsHolder: validUploadsHolders[availableHolderIndex],
                    });
                    const bucketFilesMap: SelectedFiles = {...selectedFiles};
                    if (!bucketFilesMap[bucketKey]) {
                        bucketFilesMap[bucketKey] = {};
                    }
                    bucketFilesMap[bucketKey][taskFile.taskId] = file;
                    this.props.setSelectedFiles(bucketFilesMap);
                }
            } else {
                const duplicateTxt: string = this.props.translate(
                    `${SingleFilePicker.translatePath}.duplicateFile.text`,
                ) as string;
                this.props.addAlerts([{
                    content: `${file.name} - ${duplicateTxt}`,
                    duration: 0,
                    type: "error",
                    key: `${file.name}-duplicate`,
                }]);
            }
        }

        if (validFiles.length === numberOfRequiredFiles) {
            const limitAlert: ArgsProps = createUploadFilesLimitAlert({
                validUploadsHolders: [],
                translatePath: filePickerTranslatePath,
                translate,
            });
            this.props.addAlerts([limitAlert]);
        } else if (fileSelectable && uploadsHolders.length && availableHolderIndex < 0) {
            const limitAlert: ArgsProps = createBucketLimitAlert({
                validUploadsHolders,
                translatePath: filePickerTranslatePath,
                translate,
            });
            this.props.addAlerts([limitAlert]);
        } else if (upload && (!fileSelectable || (uploadsHolders.length && availableHolderIndex < 0))) {
            const reopenedAlert: ArgsProps = createReopenedFileAlert({
                validUploadsHolders: uploadsHolders,
                translatePath: filePickerTranslatePath,
                translate,
                upload,
            });
            this.props.addAlerts([reopenedAlert]);
        }

        this.props.setJobMaskData({visible: false, displaySpinner: false});
    }

    previewUrl(url: Nullable<string>): boolean | void {
        return !!url && window.open(url!, "_blank")?.focus();
    }

    previewFile(uploadFile: Nullable<UploadFile>): boolean | void {
        return this.previewUrl(uploadFile?.thumbUrl);
    }

    isDisabledFileDisplayPreview(fileName: string): boolean {
        let isDisabled: boolean = true;
        if (fileName) {
            const dot: number = fileName.indexOf(".");
            if (dot > -1) {
                const extension: string = fileName.substring(dot + 1);
                if (extension) {
                    isDisabled = !HTML_SUPPORTED_FILE_EXTENSIONS.includes(extension);
                }
            }
        }
        return isDisabled;
    }

    /**
     * Creates Delete icon with appropriate aria-label attribute.
     *
     * @param {string} fileName
     * @return {React.ReactNode}
     */
    renderDeleteIcon(fileName: string): React.ReactNode {
        const {translate}: Props = this.props;
        return createDeleteIcon({
            translatePath: "components.FilePicker.FilePicker",
            translate,
            fileName,
        });
    }

    /**
     * Creates Preview icon with appropriate aria-label attribute.
     *
     * @param {string} fileName
     * @return {React.ReactNode}
     */
    renderPreviewIcon(fileName: string): React.ReactNode {
        const {translate}: Props = this.props;
        return createPreviewIcon({
            translatePath: "components.FilePicker.FilePicker",
            translate,
            fileName,
        });
    }

    renderFileDisplay() {
        const {
            signedMediaUrl, showOverlay, isTaskEditable, fileName,
        }: Props = this.props;
        const fileDisplayName: string = fileName || getNameFromMediaUrl(signedMediaUrl);
        const disabledFileDisplayPreview: boolean = this.isDisabledFileDisplayPreview(fileDisplayName);
        return (
            <div className="df-file-display">
                <div className="df-file-display-fallback">
                    <FileOutlined/>
                </div>
                <div className="df-file-content">
                    <div
                        className="df-file-container"
                        style={{
                            backgroundImage: `url("${signedMediaUrl}")`,
                        }}
                    />
                    <div className="df-file-name" title={fileDisplayName}>{fileDisplayName}</div>
                </div>
                {isTaskEditable && showOverlay && (
                    <div className="df-control-wrapper">
                        <div className="df-control-overlay">
                            <Button
                                hidden={disabledFileDisplayPreview}
                                onClick={this.previewUrl.bind(this, signedMediaUrl)}
                                className="df-control"
                            >
                                {this.renderPreviewIcon(fileDisplayName)}
                            </Button>
                            <Button
                                onClick={this.removeSignedMediaUrl}
                                className="df-control"
                            >
                                {this.renderDeleteIcon(fileDisplayName)}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    render(): React.ReactElement {
        const {translatePath}: typeof SingleFilePicker = SingleFilePicker;
        const {
            translate,
            fileSizeLimit,
            fileSelectionType,
            activeTaskFile,
            showOverlay,
            uploadsData: {acceptedMimeTypes},
            signedMediaUrl,
            activeTaskFileSelected,
            editor,
            uploadedFileIsSelected,
            tasks,
            activeTask,
            isReadOnly,
        }: Props = this.props;
        const clickOrDragMessage: React.ReactNode = <Translate id={`${translatePath}.clickOrDragFile.text`}/>;
        const pleaseSelectLoc: string = translate(`${translatePath}.pleaseSelectSingle.text`) as string;
        const pleaseSelectMessage: string = formatString(pleaseSelectLoc, fileSizeLimit.toString());
        const uploadVisible: boolean = !!activeTaskFile;
        const fileDisplayVisible: boolean = !!signedMediaUrl && !activeTaskFileSelected && !uploadedFileIsSelected;
        const draggerVisible: boolean = !fileDisplayVisible && !activeTaskFile;
        let uploadDisabled: boolean = isReadOnly;
        const activeTaskEntity: Nullable<TaskEntity> = tasks.find(
            (task: TaskEntity): boolean => task.payload === activeTask?.id,
        );
        if (activeTaskEntity) {
            const status: Status = getTaskPhaseStatus(activeTaskEntity);
            if (status === Status.REJECTED) {
                uploadDisabled = true;
            }
        }
        const fileList: UploadFile[] = activeTaskFile ? [activeTaskFile] : [];
        const acceptAttribute: string = `${acceptedMimeTypes.join(",")}`;
        const disablePreview: boolean = activeTaskFile
            ? ![...HTML_SUPPORTED_AUDIO, ...HTML_SUPPORTED_IMAGE].includes(activeTaskFile.type!) : false;
        const filePickerClass: string = classNames({
            "df-file-picker": true,
            "df-single-file-picker": fileSelectionType === FileSelectionType.SINGLE,
            "df-full-height": !editor?.spanReferences?.length,
        });

        return (
            <div className={filePickerClass}>
                {uploadVisible && (
                    <Upload
                        accept={acceptAttribute}
                        listType="picture-card"
                        multiple={false}
                        beforeUpload={beforeUploadHandler}
                        previewFile={generatePreviewFile}
                        iconRender={renderFallbackIcon}
                        isImageUrl={checkIfImageUrl}
                        itemRender={renderThumbnail.bind(null, Thumbnails.VIDEO_PLAYABLE)}
                        onChange={this.onChangeHandler}
                        fileList={fileList}
                        showUploadList={{
                            showPreviewIcon: false,
                            showRemoveIcon: false,
                        }}
                        disabled={isReadOnly}
                    />
                )}
                {draggerVisible && (
                    <Dragger
                        multiple={false}
                        className="df-upload-field"
                        beforeUpload={beforeUploadHandler}
                        onChange={this.onChangeHandler}
                        showUploadList={false}
                        disabled={uploadDisabled}
                    >
                        <div className="df-upload-field-content">
                            <div className="df-upload-drag"/>
                            <div className="df-click-drag-info">
                                {clickOrDragMessage}
                            </div>
                            <div className="df-please-select">
                                {pleaseSelectMessage}
                            </div>
                        </div>
                    </Dragger>
                )}
                {activeTaskFile && showOverlay && (
                    <div className="df-control-wrapper">
                        <div className="df-control-overlay">
                            <Button
                                hidden={disablePreview}
                                onClick={this.previewFile.bind(this, activeTaskFile)}
                                className="df-control"
                            >
                                {this.renderPreviewIcon(activeTaskFile.name)}
                            </Button>
                            <Button
                                onClick={this.removeFile.bind(this, activeTaskFile)}
                                className="df-control"
                            >
                                {this.renderDeleteIcon(activeTaskFile.name)}
                            </Button>
                        </div>
                    </div>
                )}
                {fileDisplayVisible && this.renderFileDisplay()}
            </div>
        );
    }
}

function mapStateToProps({
    localize,
    schema: {
        elementsValuesMap,
        parsedSchemas,
    },
    project,
    uploadFiles: {
        selectedFiles,
        finishedUploads: {
            finishedTaskIds,
            bucketCount,
        },
    },
    layout: {
        content: {
            fileSelectionType,
        },
        filePicker: {
            showOverlay,
        },
    },
    job: {
        activeTask,
        tasks,
        jobPhase,
        activeTaskPrivates: {
            signedMediaUrl,
            fileName,
            upload,
        },
    },
}: StoreState): StateProps {
    const historyParams: Nullable<HistoryParams> = getHistoryParams();
    const schemaKey: string = `${project.projectDetails?.schemaId}_${historyParams?.phase}`;
    const editor: Nullable<EditorType> = parsedSchemas[schemaKey] as EditorType;
    const taskId: string = activeTask?.id || "";
    const numberOfRequiredFiles: number = calculateNumberOfTasks(jobPhase?.phaseStats);
    const uploadBucket: UploadsBucket = mergeUploadBuckets(selectedFiles);
    const collectionRefElements: RefElementsMap = editor?.refElements || {};
    const elementsValues: ElementsValues = getElementsValues({
        metadataId: historyParams?.jobId,
        elementsValuesType: ElementsValuesType.JOB,
        schemaKey,
        elementsValuesMap,
    });
    const uploadedFileIsSelected: boolean = new Set<Nullable<string>>(
        Object.values(uploadBucket).map((uploadFile: UploadFile): string => uploadFile.name),
    ).has(fileName);
    const translate: TranslateFunction = getTranslate(localize);
    const activeTaskFileSelected: boolean = activeTask?.id ? !!uploadBucket[activeTask.id] : false;
    const validBatchUploadsConfig: boolean = editor?.batchUploads && editor?.uploadReference
        ? batchUploadsAreValid(editor.batchUploads, editor.uploadReference)
        : false;

    return {
        translate,
        fileSizeLimit: editor?.fileSizeLimitMegaBytes || 0,
        collectionRefElements,
        elementsValues,
        activeTaskFile: uploadBucket[taskId],
        activeTask,
        tasks,
        mappedTaskIds: Object.keys(uploadBucket),
        numberOfRequiredFiles,
        selectedFiles,
        fileSelectionType,
        showOverlay,
        batchUploads: editor?.batchUploads || [],
        uploadsData: initUploadsData({
            editor,
            collectionRefElements,
            elementsValues,
            translate,
            translationPath: "components.FilePicker.FilePicker",
            tasks,
            elementsValuesMap,
            schemaKey,
            uploadBucket,
        }),
        signedMediaUrl,
        fileName,
        upload,
        isTaskEditable: isEditableTask(activeTask, tasks),
        activeTaskFileSelected,
        validBatchUploadsConfig,
        editor,
        finishedTaskIds,
        uploadedFileIsSelected,
        bucketCount,
        isReadOnly: isPhaseExpired(jobPhase.accessStatus),
    };
}

const mapDispatchToProps: DispatchProps = {
    setJobMaskData,
    setSelectedFiles,
    removeSelectedFile,
    updateActiveTaskPrivates,
    clearAlerts,
    addAlerts,
};

export default connect(mapStateToProps, mapDispatchToProps)(SingleFilePicker);
